import { useState } from 'react';
import {
  adeRisk, cdiffRisk, deliriumRisk, isolationRisk, medicationReview, medicationReviewOutcome, pressureInjuryRisk,
  type MedReviewOutcome, type RiskIndicator,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';

// Aithora Home's health watch and medication review, restored from the Flutter production
// Home (home_view.dart:529-643, medication_review_panel.dart). The design's My home shows
// steadiness through the score; these are the other wellness indicators production showed
// (delirium, pressure injury, C. diff, adverse drug event, isolation) and the review "to
// discuss with your doctor" with the doctor's outcome. Each indicator answers for itself:
// switched off, building a baseline or not enough data is said in words, never a number.

const CARD = 'grid-column:1/-1;box-sizing:border-box;min-width:0;padding:18px 20px;border-radius:18px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);';
const TITLE = 'font-size:19px;font-weight:700;color:#22272E;letter-spacing:-.01em;';
const NOTE = 'font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;';
const TIER_C: Record<string, string> = { HIGH: '#B3261E', ELEVATED: '#8A5A00', WATCH: '#5B6B7C' };
/** No em-dashes in what we show (Andrew's rule), whatever the source wording carries. */
const plain = (s: string | null | undefined) => (s ?? '').replace(/\s*\u2014\s*/g, ': ');

const INDICATORS = [
  ['delirium', 'Sudden confusion', deliriumRisk],
  ['pressure', 'Skin and pressure', pressureInjuryRisk],
  ['cdiff', 'Stomach upset after antibiotics', cdiffRisk],
  ['ade', 'A new medicine not agreeing', adeRisk],
  ['isolation', 'Keeping in touch', isolationRisk],
] as const;

function stateText(r: RiskIndicator | null, err: boolean): [string, string] {
  if (err) return ['not available', '#54606C'];
  if (!r) return ['checking', '#54606C'];
  if (r.state === 'disabled') return ['switched off', '#54606C'];
  if (r.state === 'building_baseline') return ['learning your usual', '#54606C'];
  if (r.state === 'insufficient_data') return ['not enough data yet', '#54606C'];
  if (!r.tier) return ['steady', '#1F6B52'];
  return [r.tier === 'HIGH' ? 'worth a call today' : r.tier === 'ELEVATED' ? 'worth a look' : 'watching', TIER_C[r.tier] ?? '#5B6B7C'];
}

function Indicator({ label, load }: { label: string; load: (signal: AbortSignal) => Promise<RiskIndicator> }) {
  const q = useQuery(load, []);
  const [open, setOpen] = useState(false);
  const r = q.data;
  const [word, color] = stateText(r, !!q.error);
  const why = r?.state === 'scored' ? r.topContributors.map((c) => plain(c.explanation)).filter(Boolean) : r?.gate ? [plain(r.gate.message)] : [];
  return (
    <div data-testid="home-indicator" style={sx('padding:10px 0;border-top:1px solid rgba(34,39,46,.1);min-width:0;')}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={sx('display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:0;border:0;background:transparent;cursor:pointer;font-family:inherit;text-align:left;')}>
        <span style={sx('width:9px;height:9px;border-radius:50%;flex:0 0 9px;background:' + color + ';')} />
        <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;font-weight:600;color:#22272E;')}>{label}</span>
        <span style={sx('font-size:14px;font-weight:700;color:' + color + ';')}>{word}</span>
      </button>
      {open && (
        <div style={sx(NOTE + 'margin:4px 0 0 19px;')}>
          {why.map((w, i) => <div key={i}>{w}</div>)}
          {r?.recommendation && <div style={sx('margin-top:4px;color:#22272E;')}>{plain(r.recommendation)}</div>}
          {r && <div style={sx('margin-top:6px;font-size:16px;')}>{plain(r.disclaimer)}</div>}
        </div>
      )}
    </div>
  );
}

export function HealthWatch({ personId, orgId, readOnly, reason }: { personId: string; orgId: string | null; readOnly: boolean; reason: string | null }) {
  const { client } = useSession();
  const query = { userId: personId, organizationId: orgId ?? undefined };
  const review = useQuery((signal) => medicationReview(client, { query, signal }), [client, personId, orgId]);
  const [msg, setMsg] = useState<string | null>(null);
  const record = async (ruleId: string, medicationId: string | null, outcome: MedReviewOutcome) => {
    setMsg(null);
    try {
      await medicationReviewOutcome(client, { body: { userId: personId, organizationId: orgId ?? undefined, ruleId, medicationId, outcome } });
      review.reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'It was not recorded.');
    }
  };
  const rv = review.data;
  return (
    <>
      <div data-testid="home-health-watch" style={sx(CARD)}>
        <div style={sx(TITLE)}>Health watch</div>
        <div style={sx(NOTE + 'margin-top:3px;')}>Early signs worth a conversation. Tap one to see why. A wellness read, not a diagnosis.</div>
        <div style={sx('margin-top:8px;')}>
          {INDICATORS.map(([k, label, fn]) => (
            <Indicator key={k} label={label} load={(signal) => fn(client, { query, signal })} />
          ))}
        </div>
      </div>
      <div data-testid="home-med-review" style={sx(CARD)}>
        <div style={sx(TITLE)}>{rv ? plain(rv.framing.title) : 'Medication Review'}</div>
        {!rv && <div style={sx(NOTE + 'margin-top:4px;')}>{review.error ? 'The review is not available right now.' : 'Loading.'}</div>}
        {rv && (
          <>
            <div style={sx(NOTE + 'margin-top:3px;')}>{plain(rv.framing.disclaimer)}</div>
            {rv.gated && <div style={sx(NOTE + 'margin-top:8px;color:#22272E;')}>{plain(rv.gateReason) || 'No medications are on file to review.'}</div>}
            {!rv.gated && rv.candidates.length === 0 && <div style={sx(NOTE + 'margin-top:8px;color:#22272E;')}>{`Nothing to raise across ${rv.medicationCount} medicines.`}</div>}
            {rv.candidates.map((c) => (
              <div key={c.ruleId + (c.medicationId ?? '')} style={sx('padding:10px 0;border-top:1px solid rgba(34,39,46,.1);min-width:0;margin-top:6px;')}>
                <div style={sx('font-size:16px;font-weight:600;color:#22272E;')}>{c.medicationName}</div>
                <div style={sx(NOTE + 'margin-top:2px;')}>{plain(c.reason)}</div>
                <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:8px;')}>
                  <span style={sx('font-size:16px;color:#54606C;')}>What the doctor said:</span>
                  {([['discussed', 'Talked it over'], ['accepted', 'Changed it'], ['declined', 'Kept it']] as [MedReviewOutcome, string][]).map(([o, l]) => (
                    <button key={o} type="button" disabled={readOnly} title={readOnly ? reason ?? undefined : undefined} aria-pressed={c.outcome === o}
                      onClick={() => void record(c.ruleId, c.medicationId, o)}
                      style={sx('display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:9px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;'
                        + (c.outcome === o ? 'background:#2F5C97;color:#fff;border:1px solid #2F5C97;' : 'background:#FBF8F1;color:#3C454F;border:1px solid rgba(34,39,46,.2);'))}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {msg && <div role="status" style={sx(NOTE + 'color:#B3261E;margin-top:6px;')}>{msg}</div>}
          </>
        )}
      </div>
    </>
  );
}
